import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, Dialog, ErrorBanner, Input, PageHeader, Select, Spinner, Textarea } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useAction } from '../../lib/hooks';
import { categoryOptions, useCategories, type Category } from './Products';
import { slugify } from './ProductEdit';

export function Categories() {
  const { can } = useAuth();
  const q = useCategories();
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const del = useAction((c: Category) => api(`/admin/categories/${c.id}`, { method: 'DELETE' }), { success: 'Category deleted', invalidate: [['categories']] });
  const cats = q.data ?? [];
  const depthOf = (c: Category): number => (c.parentId ? 1 + depthOf(cats.find((x) => x.id === c.parentId) ?? { ...c, parentId: null }) : 0);
  const ordered = categoryOptions(cats).map((o) => cats.find((c) => c.id === o.value)!);
  return (
    <>
      <PageHeader title="Categories" actions={can('categories.manage') && <Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" />New category</Button>} />
      <Card>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <ul className="divide-y divide-cream-border">
            {ordered.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" style={{ paddingLeft: `${depthOf(c) * 1.5}rem` }}>
                <div><span className="font-medium">{c.name}</span> <span className="text-xs text-ink-muted">/{c.slug} · {c.productCount} products</span></div>
                <div className="flex items-center gap-2"><Badge status={c.status} />
                  {can('categories.manage') && <><Button variant="ghost" onClick={() => setEditing(c)}>Edit</Button><Button variant="ghost" onClick={() => confirm(`Delete ${c.name}?`) && del.mutate(c)}>Delete</Button></>}
                </div>
              </li>
            ))}
            {!ordered.length && <li className="py-6 text-center text-sm text-ink-muted">No categories yet.</li>}
          </ul>
        )}
      </Card>
      {editing && <CategoryDialog category={editing === 'new' ? null : editing} all={cats} onClose={() => setEditing(null)} />}
    </>
  );
}

function CategoryDialog({ category, all, onClose }: { category: Category | null; all: Category[]; onClose: () => void }) {
  const [f, setF] = useState({ name: category?.name ?? '', slug: category?.slug ?? '', parentId: category?.parentId ?? '', description: category?.description ?? '', status: category?.status ?? 'ACTIVE', displayOrder: String(category?.displayOrder ?? 0), seoTitle: category?.seoTitle ?? '', seoDescription: category?.seoDescription ?? '' });
  const m = useAction(() => {
    const body = { name: f.name.trim(), slug: f.slug, parentId: f.parentId || null, description: f.description.trim() || null, status: f.status, displayOrder: Number(f.displayOrder) || 0, seoTitle: f.seoTitle.trim() || null, seoDescription: f.seoDescription.trim() || null };
    return category ? api(`/admin/categories/${category.id}`, { method: 'PATCH', body }) : api('/admin/categories', { method: 'POST', body });
  }, { success: 'Category saved', invalidate: [['categories']], onSuccess: onClose });
  return (
    <Dialog open title={category ? 'Edit category' : 'New category'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={m.isPending} disabled={f.name.trim().length < 2 || !f.slug} onClick={() => m.mutate(undefined)}>Save</Button></>}>
      <Input label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: category ? f.slug : slugify(e.target.value) })} />
      <Input label="URL slug" value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} />
      <Select label="Parent" value={f.parentId} onChange={(e) => setF({ ...f, parentId: e.target.value })} options={[{ value: '', label: 'None (top level)' }, ...categoryOptions(all).filter((o) => o.value !== category?.id)]} />
      <Textarea label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <Select label="Status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} options={[{ value: 'ACTIVE', label: 'Visible' }, { value: 'INACTIVE', label: 'Hidden' }]} />
        <Input label="Sort order" type="number" min={0} value={f.displayOrder} onChange={(e) => setF({ ...f, displayOrder: e.target.value })} />
      </div>
      <Input label="SEO title" maxLength={70} value={f.seoTitle} onChange={(e) => setF({ ...f, seoTitle: e.target.value })} />
      <Textarea label="SEO description" maxLength={170} value={f.seoDescription} onChange={(e) => setF({ ...f, seoDescription: e.target.value })} />
    </Dialog>
  );
}
